export interface MediaReference {
  model: string;
  documentId: string;
  field: string;
  domains: string[];
  shared: boolean;
}

export interface ServerMedia {
  id: string;
  account: string;
  public_id: string;
  secure_url: string;
  resource_type: 'image' | 'video';
  format: string;
  bytes: number;
  width: number;
  height: number;
  duration: number | null;
  created_at: string;
  folder: string;
  usageStatus: 'inUse' | 'unused' | 'unknown';
  usageCount: number | null;
  references: MediaReference[];
  domains: string[];
  shared: boolean;
}

export interface MediaDomain {
  slug: string;
  url: string;
  customDomain: string;
}

export interface MediaInventory {
  assets: ServerMedia[];
  domains: MediaDomain[];
  usageError: string | null;
  checkedAt: string | null;
}

export type MediaStreamEvent =
  | { type: 'start' }
  | { type: 'assets'; assets: ServerMedia[] }
  | { type: 'usage'; assets: ServerMedia[]; domains: MediaDomain[]; checkedAt: string }
  | { type: 'warning' | 'error'; message: string }
  | { type: 'done'; complete: boolean };

export function mergeMediaAssets(current: ServerMedia[], updates: ServerMedia[], protectedIds: Set<string>) {
  const assets = new Map(current.map((asset) => [asset.id, asset]));
  for (const asset of updates) {
    if (!protectedIds.has(asset.id)) assets.set(asset.id, { ...assets.get(asset.id), ...asset });
  }
  return [...assets.values()];
}

export async function readMediaStream(stream: ReadableStream<Uint8Array>, onEvent: (event: MediaStreamEvent) => void) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let terminal = false;
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      let newline;
      while ((newline = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newline);
        buffer = buffer.slice(newline + 1);
        if (!line.trim()) continue;
        if (terminal) throw new Error('Unexpected media data after completion');
        const event: MediaStreamEvent = JSON.parse(line);
        if (!event || !['start', 'assets', 'usage', 'warning', 'error', 'done'].includes(event.type)) {
          throw new Error('Invalid media loading response');
        }
        onEvent(event);
        if (event.type === 'done') terminal = true;
      }
      if (done) {
        if (!terminal || buffer.trim()) throw new Error('Media loading ended before completion');
        return;
      }
    }
  } catch (error) {
    await reader.cancel(error);
    throw error;
  } finally {
    reader.releaseLock();
  }
}

export interface MediaDeleteResult {
  id: string;
  status: 'deleted' | 'missing' | 'blocked' | 'failed';
  message?: string;
  asset?: Partial<ServerMedia> & Pick<ServerMedia, 'id'>;
}

export const MAX_DELETE_BATCH = 50;
export const MEDIA_ACCEPT = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/avif',
  'video/mp4', 'video/webm', 'video/quicktime',
];
export const DEFAULT_MEDIA_FILTERS = {
  search: '', domain: '', type: '', usage: '', format: '', account: '', folder: '',
  minSize: '', maxSize: '', after: '', before: '', sort: 'newest',
};
export type MediaFilters = typeof DEFAULT_MEDIA_FILTERS;

export function filterServerMedia(assets: ServerMedia[], filters: MediaFilters, domains: MediaDomain[]) {
  const needle = filters.search.trim().toLowerCase();
  const domainNeedle = filters.domain.trim().toLowerCase();
  const exactDomains = domains.filter((domain) => (
    [domain.slug, domain.url, domain.customDomain].some((value) => value.toLowerCase() === domainNeedle)
  ));
  const matchingDomains = new Set((exactDomains.length ? exactDomains : domains.filter((domain) => (
    [domain.slug, domain.url, domain.customDomain].some((value) => value.toLowerCase().includes(domainNeedle))
  ))).map((domain) => domain.slug));
  const filtered = assets.filter((asset) => {
    if (needle && !`${asset.public_id} ${asset.secure_url}`.toLowerCase().includes(needle)) return false;
    if (filters.type && asset.resource_type !== filters.type) return false;
    if (filters.usage && asset.usageStatus !== filters.usage) return false;
    if (filters.format && asset.format !== filters.format) return false;
    if (filters.account && asset.account !== filters.account) return false;
    if (filters.folder && asset.folder !== (filters.folder === '@root' ? '' : filters.folder)) return false;
    if (filters.minSize && asset.bytes < Number(filters.minSize) * 1024 * 1024) return false;
    if (filters.maxSize && asset.bytes > Number(filters.maxSize) * 1024 * 1024) return false;
    const day = asset.created_at.slice(0, 10);
    if (filters.after && day < filters.after) return false;
    if (filters.before && day > filters.before) return false;
    if (domainNeedle === '@shared') return asset.shared;
    if (domainNeedle === '@unassigned') return asset.usageStatus === 'unused';
    if (domainNeedle === '@unknown') return asset.usageStatus === 'unknown';
    return !domainNeedle || asset.domains.some((slug) => (
      matchingDomains.has(slug) || (!exactDomains.length && slug.toLowerCase().includes(domainNeedle))
    ));
  });
  return filtered.sort((a, b) => {
    let order = 0;
    switch (filters.sort) {
      case 'oldest': order = a.created_at.localeCompare(b.created_at); break;
      case 'largest': order = b.bytes - a.bytes; break;
      case 'smallest': order = a.bytes - b.bytes; break;
      case 'name': order = a.public_id.localeCompare(b.public_id); break;
      default: order = b.created_at.localeCompare(a.created_at);
    }
    return order || a.id.localeCompare(b.id);
  });
}

export function formatMediaSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${Number((bytes / 1024 ** power).toFixed(2))} ${units[power]}`;
}

export function usageLabel(status: ServerMedia['usageStatus']) {
  return status === 'inUse' ? 'In use' : status === 'unused' ? 'No CMS references' : 'Unknown';
}

export function mediaThumbnail(asset: ServerMedia) {
  const url = new URL(asset.secure_url);
  const marker = `/${asset.resource_type}/upload/`;
  if (url.hostname !== 'res.cloudinary.com' || !url.pathname.includes(marker)) return null;
  url.pathname = url.pathname.replace(marker, `${marker}c_fill,w_128,h_128,q_auto/`);
  if (asset.resource_type === 'video') url.pathname = url.pathname.replace(/\.[^/.]+$/, '.jpg');
  return url.toString();
}
